// Admin Dashboard — plan §4.2.2 + design ref #screen-dashboard-admin.
// Sistem özeti metrikleri + 4-kanal sağlık widget + AI maliyet placeholder + aktivite log placeholder.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type {
  AIMonthlyCostResponse,
  ClientsListResponse,
  ProjectsListResponse,
  UsersListResponse,
} from "@mahir/shared";
import { api } from "../../api/client";
import { ACTIVITY_COPY, describeAuditEntry } from "../../lib/audit-labels";
import { formatUsd } from "../../lib/format-usd";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { REPORT_CHECK_COPY } from "../../lib/report-check-copy";
import { useAuthStore } from "../../stores/auth-store";

type HealthStatus = "ok" | "degraded" | "down" | "unknown";

interface HealthChannel {
  key: "api" | "db" | "storage" | "llm";
  label: string;
  path: string;
  status: HealthStatus;
  detail?: string | undefined;
}

export function AdminDashboard(): JSX.Element {
  const user = useAuthStore((s) => s.user)!;
  const [projects, setProjects] = useState<ProjectsListResponse | null>(null);
  const [users, setUsers] = useState<UsersListResponse | null>(null);
  const [clientsTotal, setClientsTotal] = useState<number | null>(null);
  const [health, setHealth] = useState<HealthChannel[]>([
    { key: "api", label: "API", path: "/health/api", status: "unknown" },
    { key: "db", label: "Veritabanı", path: "/health/db", status: "unknown" },
    { key: "storage", label: "Depolama", path: "/health/storage", status: "unknown" },
    { key: "llm", label: "LLM", path: "/health/llm", status: "unknown" },
  ]);

  useEffect(() => {
    void Promise.all([
      api<ProjectsListResponse>("/projects?limit=1"),
      api<UsersListResponse>("/admin/users").catch(() => null),
      api<ClientsListResponse>("/clients?limit=1").catch(() => null),
    ]).then(([p, u, c]) => {
      setProjects(p);
      setUsers(u);
      setClientsTotal(c?.total ?? null);
    });
  }, []);

  useEffect(() => {
    let aborted = false;
    void Promise.all(
      health.map(async (h) => {
        try {
          const res = await fetch(`/api/v1${h.path}`, { credentials: "include" });
          const body = (await res.json()) as { status?: string; message?: string };
          return {
            ...h,
            status: (body.status === "ok"
              ? "ok"
              : body.status === "degraded"
                ? "degraded"
                : "down") as HealthStatus,
            detail: body.message,
          };
        } catch {
          return { ...h, status: "down" as HealthStatus };
        }
      }),
    ).then((updated) => {
      if (!aborted) setHealth(updated);
    });
    return () => {
      aborted = true;
    };
    // health state değişir değişmez tekrar fetch'i önlemek için mount-only.
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Günaydın" : hour < 18 ? "İyi günler" : "İyi akşamlar";

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)" }}>
      <header className="mb-6">
        <div
          style={{
            fontSize: "0.72rem",
            color: "var(--color-primary)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontWeight: 600,
          }}
        >
          ADMİN KONTROL PANELİ
        </div>
        <h1
          className="text-4xl mt-1"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
        >
          {greeting}, {user.fullName}.
        </h1>
        <p className="mt-2 text-sm" style={{ color: "var(--color-text-2)" }}>
          Sistem sağlığı, kullanıcı yönetimi ve operasyonel metrikler.
        </p>
      </header>

      {/* Sistem özeti */}
      {/* Ö5 — dar ekranda 2 kolon, ≥1024 px 4 kolon. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-6xl mb-6">
        <Metric label="Toplam Proje" value={projects?.metrics.total ?? "—"} tone="default" />
        <Metric
          label="Aktif Kullanıcı"
          value={users?.users.filter((u) => u.isActive).length ?? "—"}
          tone="primary"
        />
        <Metric label="Toplam Müşteri" value={clientsTotal ?? "—"} />
        <Metric label="Sürüyor" value={projects?.metrics.inProgress ?? "—"} tone="success" />
      </div>

      {/* Ö5 — ≤1100 px'te sağ sütun alta iner; kolonlar minmax(0,…) ile taşmaz. */}
      <div className="grid gap-6 max-w-6xl grid-cols-1 min-[1101px]:grid-cols-[minmax(0,1fr)_minmax(0,360px)]">
        {/* Sistem sağlığı — 4 kanal */}
        <section>
          <h2
            className="text-lg mb-3"
            style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
          >
            Sistem Sağlığı
          </h2>
          <div
            className="rounded-xl overflow-hidden"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
            }}
          >
            <div className="grid grid-cols-2">
              {health.map((h, i) => (
                <HealthCell key={h.key} channel={h} borderLeft={i % 2 === 1} borderTop={i >= 2} />
              ))}
            </div>
          </div>
        </section>

        {/* Sağ: AI maliyet widget placeholder + son aktivite */}
        <aside className="flex flex-col gap-4">
          <div
            className="rounded-xl p-4"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
            }}
          >
            <AIMonthlyCostWidget />
          </div>

          <div
            className="rounded-xl p-4"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
            }}
          >
            <div
              style={{
                fontSize: "0.72rem",
                color: "var(--color-text-3)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 8,
              }}
            >
              Hızlı Erişim
            </div>
            <div className="grid gap-2">
              <Link
                to="/admin/users"
                style={{ fontSize: "0.88rem", color: "var(--color-primary)" }}
              >
                → Kullanıcı Yönetimi
              </Link>
              <Link
                to="/admin/frameworks"
                style={{ fontSize: "0.88rem", color: "var(--color-primary)" }}
              >
                → Çerçeve Yöneticisi
              </Link>
              <Link to="/projects" style={{ fontSize: "0.88rem", color: "var(--color-primary)" }}>
                → Tüm Projeler
              </Link>
              <Link to="/clients" style={{ fontSize: "0.88rem", color: "var(--color-primary)" }}>
                → Tüm Müşteriler
              </Link>
            </div>
          </div>
        </aside>
      </div>

      <RecentActivityWidget />
    </div>
  );
}

interface ActivityItem {
  id: number;
  action: string;
  actorName: string | null;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
  metadata: unknown;
}

function RecentActivityWidget(): JSX.Element {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api<{ items: ActivityItem[] }>("/admin/dashboard/recent-activity?limit=20")
      .then((res) => setItems(res.items))
      .catch(() => {
        // 403 ise widget gizli kalır
      })
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || items.length === 0) return <></>;

  return (
    <div
      className="mt-6 max-w-6xl rounded-xl p-5"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <h2
        className="text-lg mb-3"
        style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
      >
        {ACTIVITY_COPY.title}
      </h2>
      <ul className="flex flex-col gap-2">
        {items.slice(0, 10).map((a) => {
          const d = describeAuditEntry(a);
          return (
            <li
              key={a.id}
              className="flex justify-between gap-3 text-xs"
              style={{ color: "var(--color-text-2)" }}
            >
              <span className="flex-1 min-w-0" style={{ overflowWrap: "anywhere" }}>
                <span
                  style={{ color: "var(--color-text)", fontWeight: 600 }}
                  title={d.rawCode ?? undefined}
                >
                  {d.label}
                </span>
                {" · "}
                {a.actorName ?? ACTIVITY_COPY.systemActor}
                {d.detail && (
                  <span style={{ color: "var(--color-text-3)", fontFamily: "var(--font-mono)" }}>
                    {" · "}
                    {d.detail}
                  </span>
                )}
              </span>
              <span style={{ color: "var(--color-text-3)", whiteSpace: "nowrap" }}>
                {new Date(a.createdAt).toLocaleString("tr-TR", {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function HealthCell({
  channel,
  borderLeft,
  borderTop,
}: {
  channel: HealthChannel;
  borderLeft: boolean;
  borderTop: boolean;
}): JSX.Element {
  const dot =
    channel.status === "ok"
      ? { color: "var(--color-success)", label: "Çalışıyor" }
      : channel.status === "degraded"
        ? { color: "var(--color-warning-text)", label: "Sınırlı" }
        : channel.status === "down"
          ? { color: "var(--color-error)", label: "Devre dışı" }
          : { color: "var(--color-text-3)", label: "Bekleniyor" };

  return (
    <div
      style={{
        padding: "14px 18px",
        borderLeft: borderLeft ? "1px solid var(--color-border)" : "none",
        borderTop: borderTop ? "1px solid var(--color-border)" : "none",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: dot.color,
          flexShrink: 0,
        }}
      />
      <div className="flex-1 min-w-0">
        <div style={{ fontSize: "0.9rem", color: "var(--color-text)", fontWeight: 500 }}>
          {channel.label}
        </div>
        <div
          style={{
            fontSize: "0.72rem",
            color: dot.color,
            fontFamily: "var(--font-mono)",
            fontWeight: 600,
          }}
        >
          {dot.label}
        </div>
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "muted" | "primary" | "success";
}): JSX.Element {
  const color =
    tone === "primary"
      ? "var(--color-primary)"
      : tone === "success"
        ? "var(--color-success)"
        : tone === "muted"
          ? "var(--color-text-3)"
          : "var(--color-text)";
  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          color: "var(--color-text-3)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          lineHeight: 1.3,
          overflowWrap: "anywhere",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.6rem",
          fontWeight: 600,
          fontFamily: "var(--font-display)",
          color,
          marginTop: 4,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function AIMonthlyCostWidget(): JSX.Element {
  const [cost, setCost] = useState<AIMonthlyCostResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let aborted = false;
    api<AIMonthlyCostResponse>("/admin/cost/monthly")
      .then((res) => {
        if (!aborted) setCost(res);
      })
      .catch((err: Error) => {
        if (!aborted) setError(err.message);
      });
    return () => {
      aborted = true;
    };
  }, []);

  if (error) {
    return <div style={{ color: "var(--color-text-3)", fontSize: "0.82rem" }}>{error}</div>;
  }
  if (!cost) {
    return <div style={{ color: "var(--color-text-3)", fontSize: "0.82rem" }}>Yükleniyor…</div>;
  }

  const over80 =
    cost.percentUsed !== null &&
    cost.thresholdPct !== null &&
    cost.percentUsed >= cost.thresholdPct;
  const budgetLabel =
    cost.budgetUsd !== null ? `${formatUsd(cost.budgetUsd)} bütçe` : "bütçe belirlenmedi";

  return (
    <>
      <div
        style={{
          fontSize: "0.72rem",
          color: "var(--color-text-3)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          marginBottom: 6,
        }}
      >
        AI Maliyet (bu ay)
      </div>
      <div
        style={{
          fontSize: "1.4rem",
          fontWeight: 600,
          fontFamily: "var(--font-display)",
          color: over80 ? "var(--color-error)" : "var(--color-text)",
        }}
      >
        {formatUsd(cost.monthlyUsd)}
      </div>
      {/* Task 13 — KK maliyet kırılımı (generation vs qc). byPurpose zorunlu alan.
          RG1 (rapor aktarımı) — üçüncü kalem: llm_call_log.purpose='report_import'.
          Rapor tutarlılık denetimi — dördüncü kalem: purpose='report_check'. */}
      <div
        className="text-xs"
        style={{ color: "var(--color-text-3)", marginTop: 2, fontFamily: "var(--font-mono)" }}
      >
        Üretim: {formatUsd(cost.byPurpose.generation)} · Kalite Kontrol:{" "}
        {formatUsd(cost.byPurpose.qc)} · Rapor aktarımı: {formatUsd(cost.byPurpose.reportImport)} ·{" "}
        {REPORT_CHECK_COPY.costLabel}: {formatUsd(cost.byPurpose.reportCheck)} ·{" "}
        {KPI_PACKAGES_COPY.suggest.costLabel}: {formatUsd(cost.byPurpose.kpiSuggest)}
      </div>
      <div
        style={{
          fontSize: "0.72rem",
          color: "var(--color-text-3)",
          marginTop: 2,
          fontFamily: "var(--font-mono)",
        }}
      >
        {cost.callCount} çağrı · {budgetLabel}
        {cost.percentUsed !== null ? ` · %${cost.percentUsed}` : ""}
      </div>
      {/* M11 — attachment metric'leri (sadece dosya yüklenmişse göster) */}
      {cost.totalAttachments > 0 && (
        <div
          style={{
            fontSize: "0.72rem",
            color: "var(--color-text-3)",
            marginTop: 4,
            fontFamily: "var(--font-mono)",
          }}
        >
          📎 {cost.attachmentCallCount} çağrıda {cost.totalAttachments} ek belge ·{" "}
          {(cost.totalAttachmentBytes / 1024 / 1024).toFixed(1)} MB
        </div>
      )}
      {over80 && (
        <div
          className="mt-2 px-2 py-1 rounded text-xs"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          Bütçenin %{cost.thresholdPct}'ine ulaşıldı — yeni provider çağrılarına dikkat.
        </div>
      )}
    </>
  );
}
